<template>
  <div id="nomination-preview">
    <overlay-loading-progress :LoadingFlag="OverlayLoadingFlag" />

    <!-- Professional Document Dialog -->
    <v-dialog
      v-model="addDocumentRecordPrompt"
      persistent
      max-width="800px"
      transition="dialog-transition"
      overlay-opacity="0.7"
    >
      <add-nomination-documents
        pageTitle="Add Nomination Documents"
        pageDescription="Add Nomination Documents"
        :addDocumentRecordPrompt="addDocumentRecordPrompt"
        @hideAddDocumentRecordPrompt="addNewDocument($event)"
        v-if="addDocumentRecordPrompt"
      ></add-nomination-documents>
    </v-dialog>

    <v-card
      class="nomination-preview-modal rounded-xl overflow-hidden elevation-24"
    >
      <!-- JCI Premium Nomination Header -->
      <div class="jci-header pa-5 d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <div
            class="header-logo-box mr-4 d-flex align-center justify-center rounded-lg"
          >
            <v-icon color="#FFFFFF" medium>mdi-trophy-award</v-icon>
          </div>
          <div>
            <div class="d-flex align-center gap-2 mb-1">
              <span
                class="sub-badge px-2 py-1 rounded text-caption font-weight-bold"
              >
                OFFICIAL REVIEW
              </span>
            </div>
            <h2 class="header-title text-h5 font-weight-black white--text mb-0">
              {{ pageDescription || "Nomination Review & Preview" }}
            </h2>
          </div>
        </div>
        <v-btn icon dark class="close-btn" @click="closePrompt">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <div class="accent-bar"></div>

      <v-card-text class="pa-6 bg-light-surface">
        <!-- Skeleton Loader -->
        <v-container class="py-4" v-if="StartupLoadingFlag">
          <v-skeleton-loader
            type="article, table-tbody, actions"
            class="rounded-xl"
          />
        </v-container>

        <!-- Main Content Area -->
        <v-container class="py-0 px-0" v-if="ResultFlag">
          <!-- Nomination Details & Aadhaar Card Side-by-Side Row -->
          <v-row class="mb-5 align-stretch">
            <!-- Left Side: Candidate Profile & Nomination Summary -->
            <v-col cols="12" md="6">
              <div
                class="summary-card pa-4 rounded-xl fill-height d-flex flex-column justify-space-between"
              >
                <div>
                  <!-- Profile Header Sub-card -->
                  <div
                    class="d-flex align-center mb-3 pa-3 rounded-lg bg-light-surface border-light"
                  >
                    <v-avatar size="60" class="profile-avatar elevation-2 mr-3">
                      <img
                        :src="
                          recordData.MemberImagePath ||
                          recordData.MemberImage ||
                          (row && row.MemberImage) ||
                          '/assets/images/white-user.png'
                        "
                        alt="Candidate Photo"
                      />
                    </v-avatar>
                    <div>
                      <h3
                        class="candidate-name text-h6 font-weight-black mb-1 text-navy"
                      >
                        {{
                          recordData.MemberFullname ||
                          recordData.MemberFullName ||
                          (row && (row.MemberName || row.MemberFullName))
                        }}
                      </h3>
                      <span
                        class="membership-badge px-3 py-1 rounded-pill text-body-2 font-weight-bold"
                      >
                        <v-icon small color="#3A67B1" class="mr-1"
                          >mdi-card-account-details</v-icon
                        >
                        ID: {{ recordData.MembershipId }}
                      </span>
                    </div>
                  </div>

                  <div class="section-title-box mb-3 d-flex align-center">
                    <v-icon color="#3A67B1" class="mr-2" medium
                      >mdi-certificate-outline</v-icon
                    >
                    <h4
                      class="text-subtitle-1 font-weight-black text-navy mb-0"
                    >
                      NOMINATION DETAILS SUMMARY
                    </h4>
                  </div>

                  <v-row class="g-3">
                    <!-- Tile 1: Designation -->
                    <v-col cols="6">
                      <div
                        class="info-tile pa-3 rounded-xl fill-height d-flex flex-column justify-center"
                      >
                        <span class="tile-label text-caption font-weight-bold"
                          >Designation</span
                        >
                        <p
                          class="tile-value text-body-2 font-weight-bold mb-0 text-truncate mt-1"
                        >
                          {{ recordData.NominationDesignationName || "N/A" }}
                        </p>
                      </div>
                    </v-col>

                    <!-- Tile 2: Application Status -->
                    <v-col cols="6">
                      <div
                        class="info-tile pa-3 rounded-xl fill-height d-flex flex-column justify-center"
                      >
                        <span class="tile-label text-caption font-weight-bold"
                          >Application Status</span
                        >
                        <div class="mt-1">
                          <v-chip
                            :color="
                              recordData.NominationStatusColor || '#3A67B1'
                            "
                            dark
                            small
                            class="font-weight-bold px-2"
                          >
                            {{ recordData.NominationStatusTxt || "Pending" }}
                          </v-chip>
                        </div>
                      </div>
                    </v-col>

                    <!-- Tile 3: NHQ Status -->
                    <v-col cols="6">
                      <div
                        class="info-tile pa-3 rounded-xl fill-height d-flex flex-column justify-center"
                      >
                        <span class="tile-label text-caption font-weight-bold"
                          >NHQ Status</span
                        >
                        <div class="mt-1">
                          <v-chip
                            :color="recordData.VerifyStatusColor || '#0097D7'"
                            dark
                            small
                            class="font-weight-bold px-2"
                          >
                            {{ recordData.VerifyStatusTxt || "Under Review" }}
                          </v-chip>
                        </div>
                      </div>
                    </v-col>

                    <!-- Tile 4: LO Certification -->
                    <v-col cols="6">
                      <div
                        class="info-tile pa-3 rounded-xl fill-height d-flex flex-column justify-center"
                      >
                        <span class="tile-label text-caption font-weight-bold"
                          >LO Certification</span
                        >
                        <div class="mt-1">
                          <v-chip
                            :color="
                              recordData.PresidentStatusColor || '#56BDA3'
                            "
                            dark
                            small
                            class="font-weight-bold px-2"
                          >
                            {{ recordData.PresidentStatusTxt || "N/A" }}
                          </v-chip>
                        </div>
                      </div>
                    </v-col>

                    <!-- Tile 5: Zone -->
                    <v-col cols="6">
                      <div
                        class="info-tile pa-3 rounded-xl fill-height d-flex flex-column justify-center"
                      >
                        <span class="tile-label text-caption font-weight-bold"
                          >Zone</span
                        >
                        <p
                          class="tile-value text-body-2 font-weight-bold mb-0 mt-1"
                        >
                          {{ recordData.ZoneName || "NA" }}
                        </p>
                      </div>
                    </v-col>

                    <!-- Tile 6: LO Name -->
                    <v-col cols="6">
                      <div
                        class="info-tile pa-3 rounded-xl fill-height d-flex flex-column justify-center"
                      >
                        <span class="tile-label text-caption font-weight-bold"
                          >Local Organization</span
                        >
                        <p
                          class="tile-value text-body-2 font-weight-bold mb-0 text-truncate mt-1"
                        >
                          {{ recordData.LomName || "N/A" }}
                        </p>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-col>

            <!-- Right Side: Exact Image Replica Aadhaar Card UI + Application Form -->
            <v-col
              cols="12"
              md="6"
              class="d-flex flex-column justify-space-between"
            >
              <!-- Aadhaar Card UI (Full Column Width & Height Fill) -->
              <div
                class="aadhaar-exact-card pa-4 position-relative bg-white rounded-xl mb-3 flex-grow-1 d-flex flex-column justify-space-between"
              >
                <div>
                  <!-- Top Right Close Icon (matching image) -->
                  <v-icon small color="#777777" class="aadhaar-close-icon"
                    >mdi-close</v-icon
                  >

                  <!-- Centered Flag & JCI INDIA Header -->
                  <div class="text-center mb-2">
                    <div class="tricolor-flag-box d-inline-block mb-1">
                      <div class="flag-stripe saffron"></div>
                      <div
                        class="flag-stripe white d-flex align-center justify-center"
                      >
                        <div class="chakra-dot"></div>
                      </div>
                      <div class="flag-stripe green"></div>
                    </div>
                    <div class="jci-india-title font-weight-black">
                      JCI INDIA
                    </div>
                  </div>

                  <!-- Main Details Section -->
                  <div
                    class="d-flex align-center pa-1 flex-wrap flex-sm-nowrap gap-2"
                  >
                    <!-- Circular Candidate Photo Avatar -->
                    <div
                      class="avatar-col text-center mx-auto mx-sm-0 mb-2 mb-sm-0"
                    >
                      <v-avatar size="80" class="aadhaar-round-avatar">
                        <img
                          :src="
                            recordData.PhotoUrl ||
                            recordData.MemberImagePath ||
                            '/assets/images/white-user.png'
                          "
                          alt="Aadhaar Photo"
                          style="object-fit: cover; width: 100%; height: 100%"
                        />
                      </v-avatar>
                    </div>

                    <!-- Candidate Info Block -->
                    <div class="details-col flex-grow-1 pl-sm-2">
                      <h3 class="candidate-title-name font-weight-black mb-1">
                        {{ recordData.AadharName || recordData.MemberFullname }}
                      </h3>

                      <div class="dob-gender-line text-body-2 mb-1">
                        <span
                          class="font-weight-medium grey--text text--darken-2"
                          >DOB:</span
                        >
                        <span class="font-weight-bold ml-1 text-dark">
                          {{
                            recordData.AadhaarDob ||
                            recordData.MemberDob ||
                            recordData.MemberDobTxt ||
                            recordData.Dob ||
                            "15-05-1991"
                          }}
                        </span>
                      </div>

                      <div class="dob-gender-line text-body-2 mb-1">
                        <span
                          class="font-weight-medium grey--text text--darken-2"
                          >Gender</span
                        >
                        <span class="font-weight-bold ml-2 text-dark">
                          {{
                            recordData.GenderTxt ||
                            recordData.Gender ||
                            recordData.MemberGender ||
                            "F"
                          }}
                        </span>
                      </div>

                      <div class="address-block">
                        <div
                          class="address-label font-weight-bold text-dark mb-0"
                        >
                          Address
                        </div>
                        <div
                          class="address-value text-body-2 font-weight-medium text-dark-grey line-height-tight"
                        >
                          {{
                            recordData.Address ||
                            recordData.MemberAddress ||
                            recordData.CityName ||
                            "1-158-H, Ottukuzhivilai, Vilavancode, Kanniyakumari, Vilavancode, Chitharal, Tamil Nadu, India, 629151"
                          }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 12-Digit Large Formatted Aadhaar Number -->
                  <div class="text-center my-2">
                    <div class="aadhaar-exact-number font-weight-black">
                      {{
                        recordData.AadharNumber ||
                        recordData.AadhaarMasked ||
                        (recordData.AadhaarNumber
                          ? recordData.AadhaarNumber.replace(
                              /(\d{4})/g,
                              "$1 "
                            ).trim()
                          : "3942  7554  8476")
                      }}
                    </div>
                  </div>
                </div>

                <div>
                  <!-- Red Solid Line -->
                  <div class="red-solid-divider mb-2"></div>

                  <!-- Bottom Tagline & Verified Chip -->
                  <div
                    class="d-flex align-center justify-space-between px-2 flex-wrap"
                  >
                    <div class="flex-grow-1 text-center pr-2">
                      <span
                        class="hindi-tagline-red font-weight-black text-body-2"
                      >
                        Aadhaar - Common Man's Right
                      </span>
                    </div>
                    <div
                      class="verified-chip-green px-3 py-1 rounded-pill d-flex align-center"
                    >
                      <v-icon x-small color="#28A745" class="mr-1"
                        >mdi-check-circle</v-icon
                      >
                      <span class="verified-text font-weight-bold text-body-2"
                        >Verified</span
                      >
                    </div>
                  </div>
                </div>
              </div>

              <!-- Nomination Application Document Section (Below Aadhaar card) -->
              <div class="application-doc-card pa-3 rounded-xl">
                <div
                  class="d-flex align-center justify-space-between flex-wrap gap-2"
                >
                  <div class="d-flex align-center">
                    <div
                      class="doc-type-icon mr-2 rounded-lg d-flex align-center justify-center"
                    >
                      <v-icon medium color="#3A67B1"
                        >mdi-file-document-outline</v-icon
                      >
                    </div>
                    <div>
                      <h4
                        class="text-subtitle-1 font-weight-black text-navy mb-0"
                      >
                        NOMINATION APPLICATION FORM
                      </h4>
                      <p class="text-body-2 grey--text mb-0">
                        Official submitted application form PDF
                      </p>
                    </div>
                  </div>

                  <template v-if="recordData.NominationApplicationPath">
                    <v-btn
                      :href="recordData.NominationApplicationPath"
                      target="_blank"
                      color="#3A67B1"
                      dark
                      rounded
                      small
                      elevation="2"
                      class="view-app-btn px-4 font-weight-bold"
                    >
                      <v-icon left x-small>mdi-file-eye-outline</v-icon>
                      View Form
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-chip color="#414042" outlined font-weight-bold small>
                      <v-icon left x-small color="error"
                        >mdi-alert-circle-outline</v-icon
                      >
                      No Document
                    </v-chip>
                  </template>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Supporting Documents Grid Section -->
          <v-row class="mb-5">
            <v-col cols="12">
              <div class="documents-section-card pa-5 rounded-xl">
                <div
                  class="section-title-box mb-4 d-flex align-center justify-space-between flex-wrap gap-2"
                >
                  <div class="d-flex align-center">
                    <v-icon color="#0097D7" class="mr-2"
                      >mdi-folder-file-outline</v-icon
                    >
                    <h4
                      class="text-subtitle-1 font-weight-black text-navy mb-0"
                    >
                      SUPPORTING NOMINATION DOCUMENTS
                    </h4>
                  </div>
                  <div class="d-flex align-center gap-2">
                    <v-btn
                      color="#0097D7"
                      dark
                      rounded
                      small
                      elevation="2"
                      class="font-weight-bold px-3 mr-2"
                      @click.prevent="addEventPhoto"
                    >
                      <v-icon left x-small>mdi-plus-circle</v-icon>
                      Add / Replace Document
                    </v-btn>
                    <v-chip
                      small
                      color="#0097D7"
                      dark
                      class="font-weight-bold px-3"
                    >
                      {{
                        (recordData.NominationDocuments &&
                          recordData.NominationDocuments.length) ||
                        0
                      }}
                      Documents
                    </v-chip>
                  </div>
                </div>

                <v-row
                  class="g-3"
                  v-if="
                    recordData.NominationDocuments &&
                    recordData.NominationDocuments.length > 0
                  "
                >
                  <v-col
                    v-for="(
                      document, docIndex
                    ) in recordData.NominationDocuments"
                    :key="docIndex"
                    cols="12"
                    sm="6"
                    md="4"
                    lg="3"
                  >
                    <div
                      class="single-doc-card pa-4 rounded-lg fill-height d-flex flex-column justify-space-between"
                    >
                      <div>
                        <div class="d-flex align-center mb-2">
                          <v-avatar
                            color="rgba(0, 151, 215, 0.1)"
                            size="36"
                            class="mr-2"
                          >
                            <v-icon small color="#0097D7"
                              >mdi-file-pdf-box</v-icon
                            >
                          </v-avatar>
                          <h5
                            class="doc-title text-subtitle-2 font-weight-black text-navy mb-0 text-truncate"
                          >
                            {{ document.DocumentTypeName }}
                          </h5>
                        </div>
                        <p
                          class="doc-desc text-caption grey--text text--darken-1 mb-3"
                        >
                          {{
                            document.DocumentDescription ||
                            "Supporting document attachment"
                          }}
                        </p>
                      </div>
                      <div class="d-flex align-center gap-1 mt-2">
                        <v-btn
                          v-if="document.PdfUrl"
                          :href="document.PdfUrl"
                          target="_blank"
                          small
                          outlined
                          color="#0097D7"
                          rounded
                          class="doc-view-btn font-weight-bold flex-grow-1"
                        >
                          <v-icon left small>mdi-open-in-new</v-icon> View
                        </v-btn>
                        <v-btn
                          icon
                          small
                          color="#3A67B1"
                          @click.prevent="addEventPhoto"
                          title="Replace Document"
                        >
                          <v-icon small>mdi-file-replace-outline</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <template v-else>
                  <div class="empty-doc-box pa-6 text-center rounded-lg">
                    <v-icon size="40" color="#EDBE38" class="mb-2"
                      >mdi-folder-open-outline</v-icon
                    >
                    <p class="text-body-2 grey--text font-weight-medium mb-3">
                      No additional supporting nomination documents uploaded.
                    </p>
                    <v-btn
                      color="#0097D7"
                      dark
                      rounded
                      small
                      elevation="2"
                      class="font-weight-bold px-4"
                      @click.prevent="addEventPhoto"
                    >
                      <v-icon left small>mdi-plus-circle</v-icon>
                      Add Document
                    </v-btn>
                  </div>
                </template>
              </div>
            </v-col>
          </v-row>

          <!-- Form Section: History, New Description & Approval Actions -->
          <v-form
            ref="form2"
            v-model="valid1"
            lazy-validation
            v-on:submit.prevent="submitForm"
            style="width: 100%"
          >
            <!-- LO Approval Section -->
            <v-row class="mb-5">
              <v-col cols="12">
                <div class="history-card pa-5 rounded-xl">
                  <div
                    class="section-title-box mb-3 d-flex align-center justify-space-between flex-wrap gap-2"
                  >
                    <div class="d-flex align-center">
                      <v-icon color="#56BDA3" class="mr-2"
                        >mdi-check-decagram-outline</v-icon
                      >
                      <h4
                        class="text-subtitle-1 font-weight-black text-navy mb-0"
                      >
                        LO APPROVAL & CERTIFICATION
                      </h4>
                    </div>
                    <div class="d-flex align-center gap-2">
                      <v-chip
                        :color="recordData.PresidentStatusColor || '#56BDA3'"
                        dark
                        small
                        class="font-weight-bold px-3"
                      >
                        {{ recordData.PresidentStatusTxt || "Pending" }}
                      </v-chip>
                    </div>
                  </div>

                  <div class="history-container pa-4 rounded-lg bg-white mb-3">
                    <div
                      v-if="recordData.ConfirmationDescriptionHistory"
                      v-html="recordData.ConfirmationDescriptionHistory"
                      class="history-content text-body-2"
                    ></div>
                    <div v-else class="text-center py-3">
                      <p class="text-body-2 grey--text font-weight-medium mb-0">
                        No LO certification / approval history recorded for this
                        nomination.
                      </p>
                    </div>
                  </div>

                  <!-- LO President Approval Status Override Radio Buttons & Reason Box -->
                  <div
                    class="lo-status-radio-box mt-4 pa-4 rounded-xl bg-white elevation-1 border-light"
                  >
                    <div class="d-flex align-center mb-2">
                      <v-icon small color="#56BDA3" class="mr-2"
                        >mdi-account-cog-outline</v-icon
                      >
                      <span class="text-subtitle-2 font-weight-black text-navy">
                        LO PRESIDENT APPROVAL STATUS & OVERWRITE REASON (NHQ)
                      </span>
                    </div>
                    <v-radio-group
                      v-model="ConfirmationStatus"
                      row
                      hide-details="auto"
                      class="mt-0 pt-0 mb-3"
                    >
                      <v-radio
                        label="Keep Existing LO Status (Do Not Overwrite)"
                        :value="0"
                        color="#78909C"
                        class="font-weight-bold mr-4"
                      ></v-radio>
                      <v-radio
                        label="LO President Approved"
                        :value="1"
                        color="#56BDA3"
                        class="font-weight-bold mr-4"
                      ></v-radio>
                      <v-radio
                        label="LO President Kept Pending"
                        :value="3"
                        color="#EDBE38"
                        class="font-weight-bold"
                      ></v-radio>
                    </v-radio-group>

                    <!-- Dedicated LO President Approval Reason Textarea -->
                    <v-textarea
                      outlined
                      dense
                      v-model="ConfirmationDescription"
                      label="LO President Approval / Overwrite Reason"
                      placeholder="Enter reason or comments for LO President approval overwrite..."
                      rows="2"
                      class="custom-review-textarea font-weight-medium text-body-2"
                      color="#56BDA3"
                      hide-details="auto"
                    ></v-textarea>
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- NHQ Review & Description History Section -->
            <v-row class="mb-5">
              <v-col cols="12">
                <div class="history-card pa-5 rounded-xl">
                  <div class="section-title-box mb-3 d-flex align-center">
                    <v-icon color="#3A67B1" class="mr-2">mdi-history</v-icon>
                    <h4
                      class="text-subtitle-1 font-weight-black text-navy mb-0"
                    >
                      NHQ REVIEW & DESCRIPTION HISTORY
                    </h4>
                  </div>

                  <div class="history-container pa-4 rounded-lg bg-white">
                    <div
                      v-if="recordData.DescriptionHistory"
                      v-html="recordData.DescriptionHistory"
                      class="history-content text-body-2"
                    ></div>
                    <div v-else class="text-center py-3">
                      <p class="text-body-2 grey--text font-weight-medium mb-0">
                        No previous review history recorded for this nomination.
                      </p>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Prominent New Description Input Section -->
            <v-row class="mb-6">
              <v-col cols="12">
                <div
                  class="input-card pa-5 rounded-xl border-light bg-white elevation-2"
                >
                  <div class="section-title-box mb-2 d-flex align-center">
                    <v-icon color="#3A67B1" class="mr-2" medium
                      >mdi-square-edit-outline</v-icon
                    >
                    <h4
                      class="text-subtitle-1 font-weight-black text-navy mb-0"
                    >
                      ADD REVIEW / APPROVAL DESCRIPTION
                      <span class="red--text">*</span>
                    </h4>
                  </div>
                  <p class="text-caption grey--text text--darken-1 mb-3">
                    Type your official review comments or approval rationale
                    below.
                  </p>

                  <v-textarea
                    outlined
                    v-model="Description"
                    :rules="DescriptionRules"
                    name="input-7-4"
                    label="Enter official review comments or approval description"
                    placeholder="Type official review remarks or reason here..."
                    rows="3"
                    class="custom-review-textarea font-weight-medium text-body-1"
                    color="#3A67B1"
                    hide-details="auto"
                  ></v-textarea>
                </div>
              </v-col>
            </v-row>

            <!-- Approval Action Bar -->
            <div
              class="action-bar-card pa-4 rounded-xl d-flex flex-wrap align-center justify-space-between gap-3 elevation-2"
            >
              <v-btn
                @click="closePrompt"
                outlined
                color="#3A67B1"
                large
                rounded
                class="cancel-btn px-6 font-weight-bold"
              >
                <v-icon left>mdi-close-circle-outline</v-icon>
                Cancel
              </v-btn>

              <div class="d-flex align-center gap-3 flex-wrap">
                <v-btn
                  :disabled="!valid1"
                  @click.prevent="submitForm(2)"
                  :loading="RejectLoadingFlag"
                  color="#EDBE38"
                  dark
                  large
                  rounded
                  elevation="3"
                  class="pending-btn px-6 font-weight-bold text-dark"
                >
                  <v-icon left>mdi-clock-alert-outline</v-icon>
                  Kept Pending
                </v-btn>

                <v-btn
                  :disabled="!valid1"
                  @click.prevent="submitForm(1)"
                  :loading="ApproveLoadingFlag"
                  color="#56BDA3"
                  dark
                  large
                  rounded
                  elevation="4"
                  class="complete-btn px-8 font-weight-bold"
                >
                  <v-icon left>mdi-check-decagram</v-icon>
                  Complete Approval
                </v-btn>
              </div>
            </div>
          </v-form>
        </v-container>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import AddNominationDocuments from "@/view/pages/erp/events/nominate-member/AddNominationDocuments.vue";

export default {
  mixins: [common],
  components: {
    AddNominationDocuments,
  },
  props: {
    showDialog: {
      type: Boolean,
      required: true,
    },
    row: {
      required: true,
    },
    category: {
      required: true,
    },
    pageDescription: {
      type: String,
    },
  },
  data() {
    return {
      recordData: [],
      TrainingDocuments: [],
      ResultFlag: false,
      StartupLoadingFlag: false,

      addDocumentRecordPrompt: false,
      EventPhotoImageLimit: 10,

      OverlayLoadingFlag: false,
      ApproveLoadingFlag: false,
      RejectLoadingFlag: false,
      ReportType: "",
      valid1: true,
      Description: "",
      DescriptionRules: [(v) => !!v || "Description is required"],
      ConfirmationDescription: "",
      ConfirmationStatus: 0,
      LOStatusItems: [
        { text: "Keep Existing LO Status (Do Not Overwrite)", value: 0 },
        { text: "LO President Approved", value: 1 },
        { text: "LO President Kept Pending", value: 3 },
      ],
    };
  },
  computed: {},
  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
    recordData: function () {
      console.log("watch recordData");
      this.StartupLoadingFlag = false;
      var tr = this.recordData;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      this.ResultFlag = n1 > 0 ? true : false;
      this.ReportType = n1 > 0 ? tr.CategoryName : "";
      this.TrainingDocuments = n1 > 0 ? tr.TrainingDocuments : [];
      this.ConfirmationStatus = 0;
    },
  },
  created() {
    // this.initialize()
  },
  methods: {
    closePrompt() {
      console.log("closePrompt called");
      this.$emit("hideDialog", true);
    },
    addEventPhoto() {
      var n2 =
        (this.recordData.NominationDocuments &&
          this.recordData.NominationDocuments.length) ||
        0;
      var m2 = this.EventPhotoImageLimit;
      if (n2 < m2) {
        this.addDocumentRecordPrompt = true;
      } else {
        var message = "Action photos maximum limit " + m2 + " reached. ";
        this.sweetAlert("error", message, false);
      }
    },
    addNewDocument(document) {
      console.log("addNewDocument called", document);
      this.addDocumentRecordPrompt = false;
      if (document && document.DocumentFlag) {
        if (!this.recordData.NominationDocuments) {
          this.$set(this.recordData, "NominationDocuments", []);
        }
        this.recordData.NominationDocuments.push(document);
        this.sweetAlert("success", "Document added successfully.", true);
      }
    },
    deleteDocument(index) {
      if (index >= 0 && this.recordData.NominationDocuments) {
        this.recordData.NominationDocuments.splice(index, 1);
        this.sweetAlert("info", "Document removed.", true);
      }
    },
    validate() {
      console.log("validate is called");
      var result = this.$refs.form.validate();
      console.log("result=" + result);
      if (result) {
        this.addRecordDetails();
      } else {
        console.log("some errors occured");
      }
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.getRecordDetails();
    },
    getRecordDetails() {
      console.log("getRecordDetails called");
      this.ResultFlag = false;
      this.StartupLoadingFlag = true;
      this.TrainingDocuments = [];
      var tr = this.row;
      console.log({ tr });
      var id = tr.NominationId;
      var list_url = "api/nomination/show";
      var where = {
        Nomination: id,
      };
      console.log("list_url=" + list_url + ", where=" + JSON.stringify(where));
      this.getTableData("recordData", list_url, where);
    },
    submitForm(Status) {
      console.log("submitForm is called");

      console.log({ Status });

      var message = "";

      var validate1 = this.$refs.form2.validate();
      console.log({ validate1 });

      var tr = this.row;
      console.log({ tr });

      var id = tr.NominationId;
      console.log(id);

      if (validate1 && Status > 0 && id > 0) {
        this.OverlayLoadingFlag = true;

        var desc = this.Description;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/nomination/admin-approval-update";

        if (Status == 1) {
          this.ApproveLoadingFlag = true;
        } else if (Status == 2) {
          this.RejectLoadingFlag = true;
        }

        let upload = new FormData();
        upload.append("UserInterface", Status);
        upload.append("Nomination", id);
        upload.append("Description", desc);
        upload.append(
          "ConfirmationStatus",
          this.ConfirmationStatus !== null &&
            this.ConfirmationStatus !== undefined &&
            this.ConfirmationStatus !== ""
            ? this.ConfirmationStatus
            : 0
        );
        upload.append(
          "ConfirmationDescription",
          this.ConfirmationDescription || ""
        );
        upload.append(
          "Lom",
          tr.LomId || (this.recordData && this.recordData.LomId) || 0
        );

        var docs = this.recordData.NominationDocuments || [];
        upload.append("NominationDocuments", JSON.stringify(docs));

        for (var j = 0; j < docs.length; j++) {
          if (docs[j].DocumentFlag && docs[j].UploadedFile) {
            upload.append(
              "ActionPhoto" + j,
              docs[j].UploadedFile,
              docs[j].UploadedFileName || "document_" + j + ".pdf"
            );
          }
        }

        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        this.StatusFlag = false;

        const thisIns = this;
        var output = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));
            thisIns.OverlayLoadingFlag = false;
            thisIns.ApproveLoadingFlag = false;
            thisIns.RejectLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.StatusFlag = true;
              thisIns.sweetAlert("success", output, true);
              thisIns.closePrompt();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
            thisIns.ApproveLoadingFlag = false;
            thisIns.RejectLoadingFlag = false;
          });
      } else {
        if (!validate1) {
          message = "Kindly fill the required fields. ";
        }
        if (Status == null || Status == "") {
          message = "Status is invalid. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap");

#nomination-preview,
#nomination-preview *,
#nomination-preview .v-btn,
#nomination-preview .v-chip,
#nomination-preview .v-card,
#nomination-preview .text-h5,
#nomination-preview .text-h6,
#nomination-preview .text-subtitle-1,
#nomination-preview .text-subtitle-2,
#nomination-preview .text-body-1,
#nomination-preview .text-body-2,
#nomination-preview .text-caption,
#nomination-preview .text-overline {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif !important;
}

#nomination-preview {
  animation: modalFadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  .text-navy {
    color: #3a67b1 !important;
  }
  .text-dark {
    color: #0a0f29 !important;
  }
  .gap-2 {
    gap: 0.5rem;
  }
  .gap-3 {
    gap: 0.75rem;
  }
  .bg-light-surface {
    background-color: #f8fafc !important;
  }

  /* JCI Premium Header */
  .jci-header {
    background: #0097d7 !important;
    position: relative;

    .header-logo-box {
      width: 44px;
      height: 44px;
      background: rgba(255, 255, 255, 0.15);
      backdrop-filter: blur(8px);
    }

    .jci-brand-badge {
      background: #0097d7;
      color: #ffffff;
      letter-spacing: 1px;
    }

    .sub-badge {
      background: #63d927 !important;
      color: #ffffff !important;
      border: 1px solid rgba(255, 255, 255, 0.3) !important;
      font-weight: 800 !important;
    }

    .close-btn {
      transition: transform 0.2s ease;
      &:hover {
        transform: rotate(90deg);
      }
    }
  }

  .accent-bar {
    height: 4px;
    background: linear-gradient(90deg, #0097d7 0%, #56bda3 50%, #edbe38 100%);
  }

  /* Profile Card */
  .profile-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 14px rgba(10, 15, 41, 0.05) !important;
    transition: transform 0.3s ease;

    .avatar-wrapper {
      position: relative;
    }

    .profile-avatar {
      border: 3px solid #3a67b1;
    }

    .candidate-name {
      color: #0a0f29;
    }

    .membership-badge {
      background: rgba(58, 103, 177, 0.08);
      color: #3a67b1;
      border: 1px solid rgba(58, 103, 177, 0.2);
    }
  }

  /* Summary Card & Tiles */
  .summary-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 14px rgba(10, 15, 41, 0.05) !important;

    .info-tile {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      transition: all 0.25s ease;

      &:hover {
        background: #ffffff;
        border-color: #3a67b1;
        box-shadow: 0 4px 12px rgba(58, 103, 177, 0.1);
      }

      .tile-label {
        font-size: 12px !important;
        font-weight: 800 !important;
        color: #414042;
        letter-spacing: 0.8px;
      }

      .tile-value {
        font-size: 14px !important;
        color: #0a0f29;
      }
    }
  }

  /* Exact Image Replica Aadhaar Card SCSS */
  .aadhaar-exact-card {
    border: 1px dashed #b0b0b0 !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
    width: 100%;
    margin: 0;

    .aadhaar-close-icon {
      position: absolute;
      top: 8px;
      right: 10px;
      cursor: pointer;
    }

    .tricolor-flag-box {
      width: 42px;
      height: 24px;
      border: 1px solid #d0d0d0;

      .flag-stripe {
        height: 8px;

        &.saffron {
          background-color: #ff9933;
        }
        &.white {
          background-color: #ffffff;
          position: relative;
        }
        &.green {
          background-color: #138808;
        }

        .chakra-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          border: 1px solid #000080;
        }
      }
    }

    .jci-india-title {
      font-size: 14px !important;
      color: #0097d7 !important;
      letter-spacing: 0.5px;
    }

    .aadhaar-round-avatar {
      border: 2px solid #e0e0e0;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

      img {
        object-fit: cover;
      }
    }

    .candidate-title-name {
      font-size: 18px !important;
      color: #2b2b2b !important;
      letter-spacing: -0.2px;
      line-height: 1.2;
    }

    .address-label {
      font-size: 13px !important;
      color: #333333 !important;
    }

    .address-value {
      font-size: 13px !important;
      color: #4a4a4a !important;
      line-height: 1.4 !important;
    }

    .aadhaar-exact-number {
      font-size: 24px !important;
      color: #2b2b2b !important;
      letter-spacing: 3.5px !important;
      font-family: inherit;
    }

    .red-solid-divider {
      height: 1.5px;
      background-color: #ff0000;
    }

    .hindi-tagline-red {
      color: #ff0000 !important;
      font-size: 14px !important;
      letter-spacing: 0.3px;
    }

    .verified-chip-green {
      background-color: #e8f5e9 !important;
      border: 1px solid #c8e6c9;

      .verified-text {
        color: #28a745 !important;
        font-size: 12px !important;
      }
    }
  }

  /* Application & Document Cards */
  .application-doc-card,
  .documents-section-card,
  .history-card,
  .input-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 14px rgba(10, 15, 41, 0.05) !important;

    .doc-type-icon {
      width: 36px;
      height: 36px;
      background: rgba(58, 103, 177, 0.1);
    }
  }

  .single-doc-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: translateY(-4px);
      background: #ffffff;
      border-color: #0097d7;
      box-shadow: 0 8px 20px rgba(0, 151, 215, 0.15) !important;
    }
  }

  .empty-doc-box {
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
  }

  .history-container {
    border: 1px solid #e2e8f0;
    max-height: 220px;
    overflow-y: auto;
    color: #414042;
  }

  .custom-review-textarea {
    .v-input__control {
      border-radius: 12px;
    }
  }

  /* Approval Action Bar */
  .action-bar-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;

    .complete-btn {
      background-color: #56bda3 !important;
      transition: all 0.25s ease;
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(86, 189, 163, 0.4) !important;
      }
    }

    .pending-btn {
      background-color: #edbe38 !important;
      color: #0a0f29 !important;
      transition: all 0.25s ease;
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(237, 190, 56, 0.4) !important;
      }
    }

    .cancel-btn {
      transition: all 0.25s ease;
      &:hover {
        background: rgba(58, 103, 177, 0.05) !important;
      }
    }
  }
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
